import { type JSX } from 'react';
import { cn } from '../../lib/utils.ts';

/**
 * Identity hues - people, never status. Status colour is the good/watch/destructive
 * token set; these exist so two people are told apart at a glance, so they are fixed
 * brand-adjacent values rather than theme tokens, and they carry white text at every
 * theme. Every entry is >= 4.5:1 against #FFFFFF (WCAG 2 AA for the 12px label);
 * tests/architecture/avatar-contrast.test.ts computes that rather than trusting this
 * sentence - the first draft of this palette shipped a 3.97:1 teal under a comment
 * claiming otherwise, and the a11y e2e caught it.
 */
const IDENTITY_HUES = [
  'bg-[#6352E5]',
  'bg-[#0A6F66]',
  'bg-[#A8442F]',
  'bg-[#7A4906]',
  'bg-[#116A33]',
  'bg-[#4F41B0]',
] as const;

/** Stable per identity: the same person keeps their colour across sessions and screens. */
function hueFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return IDENTITY_HUES[hash % IDENTITY_HUES.length]!;
}

/** First letters of the first two words - "Priya Nair" → PN, "Sam" → S. */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const letters = words.slice(0, 2).map((w) => w[0]!.toUpperCase());
  return letters.join('');
}

export type AvatarProps = {
  readonly name: string;
  /**
   * Colour seed; defaults to `name`. Pass a user id where one exists (AppHeader does).
   * Never a row id that repeats the same person - seeding case rows on the CASE id gave
   * one client two colours. Honest limit of the default: a name is not an identity, so a
   * rename rehues and two same-named people collide. Case rows have no better seed until
   * `CaseSummary` carries the service-recipient id (STATUS.md records that).
   */
  readonly seed?: string;
  readonly className?: string;
  readonly 'data-testid'?: string;
};

export function Avatar({ name, seed, className, ...rest }: AvatarProps): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
        'text-xs font-semibold text-white',
        hueFor(seed ?? name),
        className,
      )}
      {...rest}
    >
      {initialsOf(name)}
    </span>
  );
}
